import React, { useState, useEffect } from 'react';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Select,
  Spinner,
  Banner,
  DataTable,
  Badge
} from '@shopify/polaris';
import { adminApi } from '../../services/api/adminApi';

export const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');
  const [serviceFilter, setServiceFilter] = useState('');

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.getAdminAnalytics(timeRange, {
        service: serviceFilter || null
      });
      
      if (response.success) {
        setAnalytics(response);
      } else {
        setError(response.error || 'Failed to fetch analytics');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange, serviceFilter]);

  const summaryCards = analytics?.summary ? [
    {
      label: 'Total Syncs',
      value: analytics.summary.totalSyncs || 0
    },
    {
      label: 'Success Rate',
      value: `${analytics.summary.successRate || 0}%`
    },
    {
      label: 'Avg Duration',
      value: `${analytics.summary.avgDuration || 0}ms`
    },
    {
      label: 'Total Records',
      value: analytics.summary.totalRecords || 0
    }
  ] : [];

  const serviceRows = analytics?.byService?.map(service => [
    service.service,
    service.syncs.toString(),
    `${service.successRate}%`,
    `${service.avgDuration}ms`,
    service.records.toString()
  ]) || [];

  const timeRangeOptions = [
    { label: 'Last 24 Hours', value: '24h' },
    { label: 'Last 7 Days', value: '7d' },
    { label: 'Last 30 Days', value: '30d' },
    { label: 'All Time', value: 'all' }
  ];

  return (
    <BlockStack gap="loose">
      {error && (
        <Banner status="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      <Card>
        <BlockStack gap="loose">
          <InlineStack align="space-between">
            <Text variant="headingMd" fontWeight="bold">Analytics</Text>
            <InlineStack gap="loose">
              <div style={{ minWidth: '200px', width: '200px', marginRight: '24px' }}>
                <Select
                  label="Time Range"
                  options={timeRangeOptions}
                  value={timeRange}
                  onChange={setTimeRange}
                />
              </div>
              <div style={{ minWidth: '200px', width: '200px' }}>
                <Select
                  label="Service"
                  options={[
                    { label: 'All Services', value: '' },
                    { label: 'Products', value: 'products' },
                    { label: 'Orders', value: 'orders' },
                    { label: 'Inventory', value: 'inventory' },
                    { label: 'Customers', value: 'customers' }
                  ]}
                  value={serviceFilter}
                  onChange={setServiceFilter}
                />
              </div>
            </InlineStack>
          </InlineStack>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spinner size="large" />
            </div>
          ) : analytics ? (
            <BlockStack gap="loose">
              {/* Summary Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px' }}>
                {summaryCards.map((card, index) => (
                  <Card key={index}>
                    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column' }}>
                      <Text variant="bodySm" tone="subdued" style={{ display: 'block', marginBottom: '12px' }}>
                        {card.label}
                      </Text>
                      <Text variant="headingLg" fontWeight="bold" style={{ display: 'block' }}>
                        {card.value}
                      </Text>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Divider between KPI cards and Service Breakdown */}
              <div style={{ height: '16px' }}></div>

              {/* Service Breakdown Table */}
              {serviceRows.length > 0 && (
                <>
                  <Card sectioned>
                    <div style={{ padding: '24px' }}>
                      <BlockStack gap="loose">
                        <Text variant="headingMd" fontWeight="bold">Service Breakdown</Text>
                        <DataTable
                          columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric']}
                          headings={['Service', 'Syncs', 'Success Rate', 'Avg Duration', 'Records']}
                          rows={serviceRows}
                        />
                      </BlockStack>
                    </div>
                  </Card>
                  {/* Divider between Service Breakdown and Time Series */}
                  <div style={{ height: '16px' }}></div>
                </>
              )}

              {/* Time Series (Simple List) */}
              {analytics.timeSeries && analytics.timeSeries.length > 0 && (
                <>
                  <Card sectioned>
                    <div style={{ padding: '24px' }}>
                      <BlockStack gap="loose">
                        <Text variant="headingMd" fontWeight="bold">Sync Activity Over Time</Text>
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          {analytics.timeSeries.map((item, index) => (
                            <div key={index} style={{ padding: '12px 0', borderBottom: index < analytics.timeSeries.length - 1 ? '1px solid #e1e3e5' : 'none' }}>
                              <InlineStack align="space-between">
                                <Text variant="bodyMd">{item.date}</Text>
                                <InlineStack gap="base">
                                  <Text variant="bodyMd">Syncs: {item.syncs}</Text>
                                  <Badge tone="success">{item.success} success</Badge>
                                  {item.failed > 0 && <Badge tone="critical">{item.failed} failed</Badge>}
                                </InlineStack>
                              </InlineStack>
                            </div>
                          ))}
                        </div>
                      </BlockStack>
                    </div>
                  </Card>
                  {/* Divider between Time Series and Errors */}
                  <div style={{ height: '16px' }}></div>
                </>
              )}

              {/* Errors */}
              {analytics.errors && analytics.errors.length > 0 && (
                <Card sectioned>
                  <div style={{ padding: '24px' }}>
                    <BlockStack gap="loose">
                      <Text variant="headingMd" fontWeight="bold">Recent Errors</Text>
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {analytics.errors.map((error, index) => (
                          <div key={index} style={{ padding: '12px 0', borderBottom: index < analytics.errors.length - 1 ? '1px solid #e1e3e5' : 'none' }}>
                            <BlockStack gap="tight">
                              <Text variant="bodyMd" fontWeight="semibold">{error.type}</Text>
                              <InlineStack gap="base">
                                <Badge>{error.service}</Badge>
                                <Text variant="bodySm" tone="subdued">Count: {error.count}</Text>
                                <Text variant="bodySm" tone="subdued">
                                  Last: {new Date(error.lastOccurred).toLocaleString()}
                                </Text>
                              </InlineStack>
                            </BlockStack>
                          </div>
                        ))}
                      </div>
                    </BlockStack>
                  </div>
                </Card>
              )}

              {serviceRows.length === 0 && (
                <Card sectioned>
                  <Text tone="subdued">No analytics data available for the selected time range.</Text>
                </Card>
              )}
            </BlockStack>
          ) : null}
        </BlockStack>
      </Card>
    </BlockStack>
  );
};

